import { RedoManagedEditableField } from "@redotech/redo-return-core-common/return-flow";
import {
  collectRedoManagedMeta,
  ExtendedReturnFlow,
  RedoManagedStepMeta,
} from "@redotech/redo-return-ui/flows/model-to-steps";
import { StepId } from "@redotech/redo-return-ui/flows/step";
import { useMemo } from "react";

/** Whitelist keys that unlock an outgoing connection (handle index), not a field. */
const EDITABLE_HANDLE_BY_FIELD: Partial<
  Record<RedoManagedEditableField, number>
> = { conditionFalseBranch: 1 };

/** Whether a whitelist unlocks the given outgoing connection handle. */
export function isHandleEditable(
  editableFields: ReadonlySet<string>,
  handleIndex: number,
  editableHandles?: ReadonlySet<number>,
): boolean {
  if (editableHandles?.has(handleIndex)) {
    return true;
  }
  for (const field of editableFields) {
    if (
      EDITABLE_HANDLE_BY_FIELD[field as RedoManagedEditableField] ===
      handleIndex
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Single source of truth for the redo-managed step lock. Redo-managed steps
 * (e.g. the package-protection section) can't be deleted or rewired
 */
export interface RedoManagedLock {
  /** Raw marker map, keyed by editor step id. Pass to `stepsToModel` on save. */
  meta: Map<StepId, RedoManagedStepMeta>;
  /** True when the flow contains at least one managed step. */
  hasManagedSteps: boolean;
  /** Marker (incl. editable-field whitelist) for a managed step, else undefined. */
  metaFor(stepId: StepId | undefined): RedoManagedStepMeta | undefined;
  /** Whether a step is managed and therefore structurally locked. */
  isStepLocked(stepId: StepId | undefined): boolean;
  /** Whether an edge is locked. Whitelisted source handles stay editable. */
  isEdgeLocked(
    source: string | null | undefined,
    target: string | null | undefined,
    sourceHandleIndex?: number,
  ): boolean;
}

export function useRedoManagedLock(model: ExtendedReturnFlow): RedoManagedLock {
  const meta = useMemo(() => collectRedoManagedMeta(model), [model]);

  return useMemo<RedoManagedLock>(() => {
    const hasManagedSteps = meta.size > 0;
    const isStepLocked = (stepId: StepId | undefined): boolean =>
      stepId != null && meta.has(stepId);
    return {
      meta,
      hasManagedSteps,
      metaFor: (stepId) => (stepId != null ? meta.get(stepId) : undefined),
      isStepLocked,
      isEdgeLocked: (source, target, sourceHandleIndex) => {
        // Start must always flow into the managed entry.
        if (hasManagedSteps && source === "start") {
          return true;
        }
        // Edges into a managed step are always locked.
        if (isStepLocked(target ?? undefined)) {
          return true;
        }
        // A locked source locks its outgoing edges except whitelisted handles.
        if (source != null && isStepLocked(source)) {
          const sourceMeta = meta.get(source);
          const fields = new Set(sourceMeta?.editableFields ?? []);
          const handles = new Set(sourceMeta?.editableHandles ?? []);
          return (
            sourceHandleIndex === undefined ||
            !isHandleEditable(fields, sourceHandleIndex, handles)
          );
        }
        return false;
      },
    };
  }, [meta]);
}
